import React, { useEffect } from "react";
import { Helmet } from "react-helmet";
import { useLocation } from "react-router-dom";
import { getHreflangLinks } from "../config/seoConfig";

/**
 * SEOMetadata Component
 * Provides comprehensive SEO meta tags for all pages
 * 
 * @param {string} title - Page title
 * @param {string} description - Meta description for SEO
 * @param {string} keywords - Comma-separated keywords
 * @param {string} ogImage - Open Graph image URL (absolute URL)
 * @param {string} canonicalUrl - Canonical URL for the page
 * @param {string} type - Open Graph type (default: "website")
 */
const SEOMetadata = ({
    title,
    description,
    keywords,
    ogImage,
    canonicalUrl,
    type = "website",
}) => {
    const location = useLocation();

    // Default values
    const siteName = "Tuatara ATV";
    const defaultImage = ogImage || `${window.location.origin}/logo192.png`;
    const fullCanonicalUrl = canonicalUrl || `${window.location.origin}${location.pathname}`;
    const fullTitle = title ? title : siteName;

    // Single source of truth for the document title. react-helmet (below) also
    // writes <title>, but its DOM update is async/debounced, so a crawler — or the
    // analytics call in usePageTitle — can read the stale static index.html title
    // before it runs. Writing it here on mount / title-change applies the correct
    // per-page title immediately and consistently across every route. Do NOT also
    // set document.title elsewhere (e.g. usePageTitle), or the two race.
    useEffect(() => {
        if (fullTitle) {
            document.title = fullTitle;
        }
    }, [fullTitle]);

    // hreflang alternates. Paired US<->NZ pages emit reciprocal en-US/en-NZ/x-default;
    // single-region pages emit a self-referential own-locale + x-default. Returns
    // null for unknown paths (no hreflang emitted).
    const hreflangAlternates = getHreflangLinks(location.pathname);
    const hreflangLinks = hreflangAlternates
        ? hreflangAlternates.map((alt) => (
            <link
                key={`hl-${alt.hrefLang}`}
                rel="alternate"
                hrefLang={alt.hrefLang}
                href={alt.href}
            />
        ))
        : null;

    const organizationSchema = {
        "@context": "https://schema.org",
        "@type": "Organization",
        "name": siteName,
        "url": window.location.origin,
        "logo": `${window.location.origin}/logo192.png`,
        "contactPoint": {
            "@type": "ContactPoint",
            "telephone": "+64-3-347-4956",
            "email": "info@tuataraatv.com",
            "contactType": "customer service"
        },
        "sameAs": [
            "https://www.facebook.com/TuataraATV",
            "https://www.youtube.com/@tuataraatvNZ"
        ]
    };

    return (
        <Helmet>
            {/* Primary Meta Tags */}
            <title>{fullTitle}</title>
            <meta name="description" content={description} />
            {keywords && <meta name="keywords" content={keywords} />}
            <link rel="canonical" href={fullCanonicalUrl} />
            {hreflangLinks}
            <link rel="icon" href="/favicon.ico" type="image/x-icon" />

            {/* Open Graph / Facebook */}
            <meta property="og:type" content={type} />
            <meta property="og:url" content={fullCanonicalUrl} />
            <meta property="og:title" content={fullTitle} />
            <meta property="og:description" content={description} />
            <meta property="og:image" content={defaultImage} />
            <meta property="og:site_name" content={siteName} />

            {/* Twitter */}
            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:url" content={fullCanonicalUrl} />
            <meta name="twitter:title" content={fullTitle} />
            <meta name="twitter:description" content={description} />
            <meta name="twitter:image" content={defaultImage} />

            {/* Organization Schema */}
            <script type="application/ld+json">
                {JSON.stringify(organizationSchema)}
            </script>
        </Helmet>
    );
};

export default SEOMetadata;
